import is from '@sindresorhus/is';
import type { SetRequired } from 'type-fest';
import { z } from 'zod';

import { jobArchitectureGenerationInfoSchema, jobArchitectureItemSchema } from './job-architecture';
import {
    creatorSchema,
    jobArchitectureProfileReviewerSchema,
    jobProfileComponentSchema,
    jobProfileLegacySchema,
} from './job-profile';
import { versionInformationSchema } from './job-profile-versions';

export const jobRoleWithTypeSchemaTransformed = jobArchitectureItemSchema
    .extend({
        actions: z
            .object({
                canAssignReviewer: z.boolean(),
                canEdit: z.boolean(),
                canPublish: z.boolean(),
                canReadDebugInfo: z.boolean(),
            })
            .optional()
            .default({
                canAssignReviewer: false,
                canEdit: false,
                canPublish: false,
                canReadDebugInfo: false,
            }),
        creator: creatorSchema.optional(),
        generationInfo: jobArchitectureGenerationInfoSchema.optional(),
        isArchived: z.boolean(),
        jobProfile: jobProfileComponentSchema.optional(),
        profileGenerationInfo: jobArchitectureGenerationInfoSchema.optional(),
        reviewer: jobArchitectureProfileReviewerSchema.optional(),
        versionInfo: versionInformationSchema.optional(),
    })
    .transform((jobRole) => ({
        ...jobRole,
        type: 'job-role' as const,
    }));

export const jobRoleWithLegacyJobProfileSchemaTransformed = jobArchitectureItemSchema
    .extend({
        actions: z
            .object({
                canAssignReviewer: z.boolean(),
                canEdit: z.boolean(),
                canPublish: z.boolean(),
                canReadDebugInfo: z.boolean(),
            })
            .optional()
            .default({
                canAssignReviewer: false,
                canEdit: false,
                canPublish: false,
                canReadDebugInfo: false,
            }),
        creator: creatorSchema.optional(),
        generationInfo: jobArchitectureGenerationInfoSchema.optional(),
        isArchived: z.boolean(),
        jobProfile: jobProfileLegacySchema,
        profileGenerationInfo: jobArchitectureGenerationInfoSchema.optional(),
        reviewer: jobArchitectureProfileReviewerSchema.optional(),
    })
    .transform((jobRole) => ({
        ...jobRole,
        type: 'job-role' as const,
    }));

export type JobRole = z.infer<typeof jobRoleWithTypeSchemaTransformed>;

export type JobRoleWithJobProfile = SetRequired<JobRole, 'jobProfile'>;

export type JobRoleWithLegacyJobProfile = z.infer<typeof jobRoleWithLegacyJobProfileSchemaTransformed>;

export type JobRoleWithReviewer = SetRequired<JobRole, 'reviewer'>;

export const isJobRoleWithJobProfile = (item: {
    readonly type: string;
    readonly jobProfile?: unknown;
}): item is JobRoleWithJobProfile => {
    return item.type === 'job-role' && is.object(item.jobProfile);
};

export const isJobRoleWithReviewer = (item: {
    readonly type: string;
    readonly reviewer?: unknown;
}): item is JobRoleWithReviewer => {
    return item.type === 'job-role' && is.object(item.reviewer);
};
